import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Bell, ChevronsUpDown, CircleUser, ExternalLink, LogOut, Search, Sparkles, UserRoundCog } from 'lucide-react';
import { filterNavItems, toNumber } from '@blimo/shared';
import { portalQueries } from '@blimo/api-client';
import { useApi, useStopImpersonation } from '@blimo/api-client/react';
import {
  BrandLogo,
  BrandingLegalLinks,
  Button,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  ThemeToggle,
  useBranding,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  initials,
  useSidebar,
} from '@blimo/ui';
import { groupNavItems, portalNav } from '../nav.js';
import { useLogout } from '../lib/use-logout.js';
import { isActivePath } from '../lib/nav-utils.js';
import { env } from '../lib/api.js';
import { HeaderSlotContext } from '../components/ui/header-slot.js';

/** Counts of GET /portal/home shown as badges (only keys the API sends; 0 = no badge). */
const BADGE_KEYS = {
  announcements: 'unread_announcements',
  messages: 'unread_message_replies',
  'document-checks': 'open_document_checks',
  'meter-readings': 'open_meter_readings',
  proxies: 'upcoming_proxies',
};

/** Wording of the notification menu per nav id. */
const NOTICE_TEXT = {
  announcements: (n) => (n === 1 ? 'ungelesene Mitteilung' : 'ungelesene Mitteilungen'),
  messages: (n) => (n === 1 ? 'neue Antwort' : 'neue Antworten'),
  'document-checks': (n) => (n === 1 ? 'offene Belegprüfung' : 'offene Belegprüfungen'),
  'meter-readings': (n) => (n === 1 ? 'offener Zähler' : 'offene Zähler'),
  proxies: (n) => (n === 1 ? 'anstehende Vollmacht' : 'anstehende Vollmachten'),
};

/** @returns {Record<string, number>} nav id → count */
function useNavBadges() {
  const api = useApi();
  const home = useQuery(portalQueries.home(api));
  return useMemo(() => {
    const tiles = home.data?.tiles ?? {};
    /** @type {Record<string, number>} */
    const badges = {};
    for (const [id, key] of Object.entries(BADGE_KEYS)) {
      const n = Object.hasOwn(tiles, key) ? toNumber(tiles[key]) : null;
      if (n && n > 0) badges[id] = n;
    }
    return badges;
  }, [home.data]);
}

/**
 * Navigation items for the user: role/permission rules (`filterNavItems`) plus items with `requires: 'assistant'`, which
 * appear only once `GET /portal/assistant/status` says `permitted` (requested only when the permission rule matched).
 * @param {import('@blimo/api-client').Me} me
 */
function useVisibleNav(me) {
  const api = useApi();
  const allowed = useMemo(() => filterNavItems(portalNav, me), [me]);
  const needsAssistant = allowed.some((item) => item.requires === 'assistant');
  const status = useQuery({ ...portalQueries.assistantStatus(api), enabled: needsAssistant });
  const permitted = status.data?.permitted === true;
  return useMemo(() => allowed.filter((item) => item.requires !== 'assistant' || permitted), [allowed, permitted]);
}

const badgeText = (n) => (n > 99 ? '99+' : String(n));

/**
 * Dashboard shell (inset sidebar): from lg a collapsible sidebar on the page background next to a rounded content panel
 * with a slim sticky header (sidebar toggle, breadcrumb of the page, Blimo AI, colour scheme, notifications, account).
 * Below lg the sidebar opens as a drawer from the header. ⌘K / Ctrl+K opens the quick jump.
 * @param {{ me: import('@blimo/api-client').Me, children: import('react').ReactNode }} props
 */
export function AppShell({ me, children }) {
  const items = useVisibleNav(me);
  const groups = useMemo(() => groupNavItems(items), [items]);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const logout = useLogout();
  const badges = useNavBadges();
  const [slot, setSlot] = useState(/** @type {HTMLElement | null} */ (null));
  const [searchOpen, setSearchOpen] = useState(false);
  const hasAssistant = items.some((item) => item.id === 'assistant');

  useEffect(() => {
    /** @param {KeyboardEvent} e */
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <SidebarProvider desktopQuery="(min-width: 1024px)" className="bg-(--shell)">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-[calc(0.75rem+env(safe-area-inset-top))] focus:z-50 focus:rounded-lg focus:border focus:bg-card focus:px-4 focus:py-2.5 focus:text-sm"
      >
        Zum Inhalt springen
      </a>
      <PortalSidebar groups={groups} pathname={pathname} badges={badges} me={me} onLogout={logout} onSearch={() => setSearchOpen(true)} />

      <SidebarInset className="min-h-dvh lg:pb-2 lg:pr-2">
        {me.impersonating && <ImpersonationBanner me={me} />}
        <div className="sticky top-0 z-30 bg-(--shell) lg:pt-2 print:hidden">
          <header className="flex h-14 items-center gap-2 border-b bg-background px-3 pt-[env(safe-area-inset-top)] sm:px-4 lg:rounded-t-xl lg:border lg:pt-0">
            <SidebarTrigger className="size-8 text-muted-foreground hover:text-foreground" />
            <Link to="/" className="flex shrink-0 items-center lg:hidden" aria-label="Zur Übersicht">
              <BrandLogo className="h-7" />
            </Link>
            <span className="mx-1 hidden h-4 w-px bg-border md:block" aria-hidden="true" />
            <div ref={setSlot} className="hidden min-w-0 flex-1 md:block" />
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {hasAssistant && <AssistantButton active={isActivePath(pathname, '/assistent')} />}
              <ThemeToggle size="icon-sm" className="size-8 text-muted-foreground hover:text-foreground" />
              <NotificationsMenu badges={badges} items={items} />
              <span className="mx-1 hidden h-4 w-px bg-border sm:block" aria-hidden="true" />
              <UserMenu me={me} onLogout={logout} variant="avatar" />
            </div>
          </header>
        </div>
        <HeaderSlotContext value={slot}>
          <main
            id="main"
            tabIndex={-1}
            className="flex flex-1 flex-col bg-background px-4 pb-10 pt-5 outline-none sm:px-6 sm:pt-6 lg:rounded-b-xl lg:border lg:border-t-0 lg:pb-8"
          >
            <div className="mx-auto flex w-full max-w-[88rem] flex-1 flex-col">{children}</div>
            <PortalFooter />
          </main>
        </HeaderSlotContext>
      </SidebarInset>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} groups={groups} hasAssistant={hasAssistant} />
    </SidebarProvider>
  );
}

/** Sidebar: logo, quick jump, nav groups with badges, account block. */
function PortalSidebar({ groups, pathname, badges, me, onLogout, onSearch }) {
  const { collapsed } = useSidebar();
  return (
    <Sidebar label="Hauptnavigation" className="print:hidden">
      <SidebarHeader className={cn('h-16 px-4 pt-1', collapsed && 'h-14 justify-center px-0 pt-0')}>
        <Link to="/" aria-label="Zur Übersicht" className="flex items-center rounded-md">
          {collapsed ? <BrandLogo variant="mark" className="h-3.5" /> : <BrandLogo className="h-10" />}
        </Link>
      </SidebarHeader>
      <div className={cn('px-3 pb-2', collapsed && 'flex justify-center px-0')}>
        <SearchButton collapsed={collapsed} onClick={onSearch} />
      </div>
      <SidebarContent className={cn('gap-0 pt-0', collapsed ? 'px-0' : 'px-1')}>
        {groups.map((group) => (
          <SidebarGroup key={group.id} aria-labelledby={`nav-group-${group.id}`}>
            <SidebarGroupLabel id={`nav-group-${group.id}`}>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const Icon = item.icon;
                const count = badges[item.id];
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild isActive={isActivePath(pathname, item.to)} tooltip={item.label}>
                      <Link to={item.to}>
                        <Icon aria-hidden="true" />
                        <span>{item.label}</span>
                        {count ? (
                          <span className="ml-auto rounded-md bg-(--primary-tint-strong) px-1.5 text-[0.6875rem] font-semibold leading-[1.125rem] text-(--primary-text) tabular-nums">
                            <span aria-hidden="true">{badgeText(count)}</span>
                            <span className="sr-only"> ({count} neu)</span>
                          </span>
                        ) : null}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter className={cn('p-2', collapsed && 'items-center')}>
        <UserMenu me={me} onLogout={onLogout} variant={collapsed ? 'avatar' : 'sidebar'} />
      </SidebarFooter>
    </Sidebar>
  );
}

/** Looks like a search field, opens the quick jump (⌘K). */
function SearchButton({ collapsed, onClick }) {
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon-sm" onClick={onClick} aria-label="Suchen und springen" className="text-muted-foreground">
            <Search aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">Suchen (⌘K)</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Suchen und springen"
      aria-keyshortcuts="Control+K Meta+K"
      className="flex h-8 w-full items-center gap-2 rounded-lg border bg-background px-2.5 text-[0.8125rem] text-muted-foreground transition-colors hover:text-foreground"
    >
      <Search className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="flex-1 text-left">Suchen …</span>
      <kbd className="rounded border bg-muted px-1 font-sans text-[0.6875rem] leading-4 text-muted-foreground" aria-hidden="true">
        ⌘K
      </kbd>
    </button>
  );
}

/** Quick jump over the navigation; with Blimo AI also „Blimo AI fragen: …“. */
function CommandPalette({ open, onOpenChange, groups, hasAssistant }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const go = (options) => {
    onOpenChange(false);
    setQuery('');
    navigate(options);
  };
  const question = query.trim();
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Suchen und springen" description="Bereich suchen oder Blimo AI fragen" shouldFilter>
      <CommandInput value={query} onValueChange={setQuery} placeholder={hasAssistant ? 'Bereich suchen oder Blimo AI fragen …' : 'Bereich suchen …'} />
      <CommandList>
        <CommandEmpty>Kein Bereich gefunden.</CommandEmpty>
        {hasAssistant && question && (
          <CommandGroup heading="Blimo AI" forceMount>
            <CommandItem value={`blimo-ai ${question}`} forceMount onSelect={() => go({ to: '/assistent', search: { q: question } })}>
              <Sparkles className="text-(--primary-text)" aria-hidden="true" />
              <span className="truncate">Blimo AI fragen: „{question}“</span>
            </CommandItem>
          </CommandGroup>
        )}
        {groups.map((group) => (
          <CommandGroup key={group.id} heading={group.label}>
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem key={item.id} value={`${item.label} ${item.shortLabel ?? ''}`} onSelect={() => go({ to: item.to })}>
                  <Icon className="text-muted-foreground" aria-hidden="true" />
                  {item.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

function AssistantButton({ active }) {
  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      className={cn('h-8 gap-1.5 px-2.5 max-sm:w-8 max-sm:px-0', active && 'bg-accent')}
      aria-current={active ? 'page' : undefined}
    >
      <Link to="/assistent" aria-label="Blimo AI" title="Blimo AI">
        <Sparkles className="text-(--primary-text)" aria-hidden="true" />
        <span className="max-sm:sr-only" aria-hidden="true">
          Blimo AI
        </span>
      </Link>
    </Button>
  );
}

/** Bell with dot; lists the open items of GET /portal/home (only visible destinations). */
function NotificationsMenu({ badges, items }) {
  const notices = items.filter((item) => badges[item.id] && NOTICE_TEXT[item.id]);
  const total = notices.reduce((sum, item) => sum + badges[item.id], 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative size-8 text-muted-foreground hover:text-foreground"
          aria-label={total ? `Hinweise (${total} neu)` : 'Hinweise'}
          title="Hinweise"
        >
          <Bell aria-hidden="true" />
          {total > 0 && <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-primary ring-2 ring-background" aria-hidden="true" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Hinweise</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notices.length === 0 ? (
          <p className="px-2 py-3 text-[0.8125rem] text-muted-foreground">Keine neuen Hinweise.</p>
        ) : (
          notices.map((item) => {
            const Icon = item.icon;
            const n = badges[item.id];
            return (
              <DropdownMenuItem key={item.id} asChild>
                <Link to={item.to}>
                  <Icon aria-hidden="true" />
                  <span className="flex-1">
                    <span className="font-semibold tabular-nums">{badgeText(n)}</span> {NOTICE_TEXT[item.id](n)}
                  </span>
                </Link>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Account menu (Profil, Verwaltung, Abmelden). `sidebar`: block with avatar, name and e-mail; `avatar`: round trigger.
 * @param {{ me: any, onLogout: () => void, variant: 'sidebar'|'avatar' }} props
 */
function UserMenu({ me, onLogout, variant }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'sidebar' ? (
          <button
            type="button"
            aria-label={`Benutzermenü für ${me.name}`}
            className="flex min-h-11 w-full min-w-0 items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-sidebar-accent"
          >
            <span className="portal-avatar size-8 text-[0.6875rem]" aria-hidden="true">
              {initials(me.name)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-[0.8125rem] font-medium">{me.name}</span>
              <span className="truncate text-xs text-muted-foreground">{me.email}</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        ) : (
          <button type="button" aria-label={`Benutzermenü für ${me.name}`} className="rounded-full">
            <span className="portal-avatar size-8 text-[0.6875rem]" aria-hidden="true">
              {initials(me.name)}
            </span>
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={variant === 'sidebar' ? 'start' : 'end'} side={variant === 'sidebar' ? 'top' : 'bottom'} className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate">{me.name}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">{me.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profil">
            <CircleUser aria-hidden="true" /> Mein Profil
          </Link>
        </DropdownMenuItem>
        {me.apps?.admin && (
          <DropdownMenuItem asChild>
            <a href={env.adminUrl}>
              <ExternalLink aria-hidden="true" /> Zur Verwaltung
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout} variant="destructive">
          <LogOut aria-hidden="true" className="text-destructive!" /> Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Benutzeransicht beenden und zurück ins Verwaltungs-CMS. */
function ImpersonationBanner({ me }) {
  const stop = useStopImpersonation({ onDone: () => window.location.assign(env.adminUrl) });
  return (
    <div
      role="status"
      className="flex items-center gap-2 bg-warning px-4 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))] text-[0.8125rem] font-medium text-warning-foreground lg:mt-2 lg:rounded-xl lg:pt-2"
    >
      <UserRoundCog className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">
        Impersonation aktiv: Sie sehen das Portal als <strong>{me.name}</strong>.
      </span>
      <Button size="sm" variant="outline" onClick={() => stop.mutate()} disabled={stop.isPending} className="bg-white/60! text-warning-foreground!">
        Beenden
      </Button>
    </div>
  );
}

/** Fußzeile: © Verwaltung (Branding) + Impressum/Datenschutz. */
function PortalFooter() {
  const { company_name: company } = useBranding();
  return (
    <footer className="mx-auto mt-10 flex w-full max-w-[88rem] flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t pt-4 text-xs text-muted-foreground print:hidden">
      <span>
        © {new Date().getFullYear()} {company}
      </span>
      <BrandingLegalLinks className="justify-end" />
    </footer>
  );
}
